import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

// ── Platform config (mirrors Scheduling.js) ───────────────────────────────────
const SCH_PLATFORMS = [
  { id:'ig',  name:'Instagram',       color:'#e1306c', bg:'#fce4ec' },
  { id:'fb',  name:'Facebook',        color:'#1877f2', bg:'#e7f0fd' },
  { id:'wa',  name:'WhatsApp Status', color:'#25d366', bg:'#e6f7ec' },
  { id:'li',  name:'LinkedIn',        color:'#0a66c2', bg:'#e1ecf7' },
  { id:'yt',  name:'YouTube',         color:'#ff0000', bg:'#fde7e7' },
  { id:'x',   name:'X / Twitter',    color:'#000000', bg:'#e7e7e7' },
  { id:'pi',  name:'Pinterest',       color:'#bd081c', bg:'#fbe1e3' },
  { id:'th',  name:'Threads',         color:'#000000', bg:'#e7e7e7' },
  { id:'gg',  name:'Google Ads',      color:'#4285f4', bg:'#e8f0fe' },
  { id:'ma',  name:'Meta Ads',        color:'#0668e1', bg:'#e7f0fd' },
  { id:'tk',  name:'TikTok',          color:'#010101', bg:'#f0f0f0' },
  { id:'tg',  name:'Telegram',        color:'#229ed9', bg:'#e3f4fc' },
];

const normStr = s => (s||'').toLowerCase().replace(/[^a-z0-9]/g,'');
const findPlatformConfig = name => SCH_PLATFORMS.find(p =>
  normStr(p.name) === normStr(name) || normStr(p.id) === normStr(name)
);

const fmtDate = str => {
  if (!str) return '—';
  return new Date(str).toLocaleString('en-IN', {
    day:'2-digit', month:'short', year:'numeric',
    hour:'2-digit', minute:'2-digit',
  });
};

// ── Platform icon (simple letter badge) ──────────────────────────────────────
function PlatBadge({ pl, liveUrl, proofUrl }) {
  const href = liveUrl || proofUrl;
  const inner = (
    <div title={pl.name} style={{
      width: 28, height: 28, borderRadius: 6,
      background: pl.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: `1.5px solid ${pl.color}44`,
      position: 'relative',
    }}>
      <span style={{ fontSize: '0.6rem', fontWeight: 800, color: pl.color }}>
        {pl.name.slice(0, 2).toUpperCase()}
      </span>
      {liveUrl && (
        <span style={{
          position: 'absolute', bottom: -3, right: -3,
          width: 10, height: 10, borderRadius: '50%',
          background: '#1db87a', border: '1.5px solid var(--bg2)',
        }} title="Live" />
      )}
    </div>
  );
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
      {inner}
    </a>
  ) : inner;
}

// ── Lightbox ─────────────────────────────────────────────────────────────────
function ImageLightbox({ src, name, onClose }) {
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.88)', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }}>
      <button onClick={onClose} style={{
        position: 'absolute', top: 16, right: 16,
        background: 'rgba(255,255,255,0.15)', color: '#fff',
        border: 'none', borderRadius: 6, cursor: 'pointer',
        padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600,
      }}>✕ Close</button>
      <div style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
        <img src={src} alt={name || 'creative'}
          style={{ maxWidth: '92vw', maxHeight: '88vh', borderRadius: 8,
            objectFit: 'contain', boxShadow: '0 8px 48px rgba(0,0,0,0.6)' }} />
        {name && <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginTop: 8 }}>{name}</div>}
      </div>
    </div>
  );
}

// ── Published Content Card ────────────────────────────────────────────────────
function PublishedCard({ wo }) {
  const [platRecs,  setPlatRecs]  = useState({});
  const [loading,   setLoading]   = useState(true);
  const [lightbox,  setLightbox]  = useState(false);

  const creative = wo.creative?.status === 'Approved' ? wo.creative : null;
  const [ey, em, ed] = (wo.event_date || '').split('-');

  useEffect(() => {
    api.get(`/scheduling/${wo.id}`)
      .then(r => {
        const map = {};
        (r.data.platforms || []).forEach(p => {
          const pl = findPlatformConfig(p.platform);
          const key = pl ? pl.id : p.platform;
          map[key] = { ...p, state: p.state === 'pending' ? undefined : p.state };
        });
        setPlatRecs(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [wo.id]);

  // Collect posted platforms
  const postedPlatforms = SCH_PLATFORMS.filter(pl => {
    const ps = platRecs[pl.id] || platRecs[pl.name];
    return ps?.state === 'posted';
  }).map(pl => {
    const ps = platRecs[pl.id] || platRecs[pl.name] || {};
    return {
      ...pl,
      liveUrl:  ps.live_url  || ps.url      || '',
      proofUrl: ps.proof_url || ps.proofUrl  || '',
      postedAt: ps.posted_at || ps.postedAt  || '',
      postedBy: ps.posted_by || ps.postedBy  || '',
    };
  });

  const firstPosted = postedPlatforms
    .filter(p => p.postedAt)
    .sort((a, b) => new Date(a.postedAt) - new Date(b.postedAt))[0];

  return (
    <div style={{
      background: 'var(--bg2)',
      border: '1px solid var(--border)',
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Green accent top bar */}
      <div style={{ height: 4, background: '#1db87a', width: '100%' }} />

      {/* Creative thumbnail */}
      <div style={{
        position: 'relative', background: 'var(--bg3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: 160, cursor: creative?.image_data ? 'zoom-in' : 'default',
        overflow: 'hidden',
      }} onClick={() => creative?.image_data && setLightbox(true)}>
        {creative?.image_data ? (
          <img src={creative.image_data} alt="creative"
            style={{ width: '100%', height: 160, objectFit: 'cover' }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
            color: 'var(--text3)', padding: 24 }}>
            <span style={{ fontSize: '2.5rem', opacity: 0.25 }}>🖼️</span>
            <span style={{ fontSize: '0.65rem' }}>No creative image</span>
          </div>
        )}
        {/* Posted badge overlay */}
        <div style={{
          position: 'absolute', top: 8, right: 8,
          background: '#1db87a', color: '#fff',
          fontSize: '0.6rem', fontWeight: 800,
          padding: '2px 8px', borderRadius: 20,
          boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
        }}>✅ Posted</div>
        {creative?.image_data && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s',
          }} className="thumb-hover">
            <span style={{ fontSize: '1.4rem', opacity: 0, transition: 'opacity 0.15s' }}>🔍</span>
          </div>
        )}
      </div>

      {/* WO info */}
      <div style={{ padding: '10px 12px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{
            fontFamily: 'Space Mono,monospace', fontSize: '0.68rem', fontWeight: 800,
            color: 'var(--accent)', background: 'rgba(59,126,240,0.08)',
            padding: '1px 7px', borderRadius: 5, border: '1px solid rgba(59,126,240,0.2)',
          }}>{wo.wo_number}</span>
          {wo.client?.name && (
            <span style={{
              padding: '1px 8px', background: 'var(--bg3)',
              border: '1px solid var(--border2)', borderRadius: 20,
              fontSize: '0.65rem', fontWeight: 700, color: 'var(--text2)',
            }}>{wo.client.name}</span>
          )}
        </div>

        <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text1)', lineHeight: 1.3 }}>
          {wo.title}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {wo.event_date && (
            <span style={{ fontSize: '0.65rem', color: 'var(--text3)', fontFamily: 'Space Mono,monospace' }}>
              📅 {ed}-{em}-{ey}
            </span>
          )}
          {wo.campaign_type?.name && (
            <span style={{
              fontSize: '0.62rem', color: 'var(--text3)',
              padding: '1px 5px', background: 'var(--bg3)',
              border: '1px solid var(--border)', borderRadius: 4,
            }}>{wo.campaign_type.name}</span>
          )}
        </div>

        {/* Posted time */}
        {firstPosted?.postedAt && (
          <div style={{ fontSize: '0.62rem', color: '#1db87a', fontWeight: 700 }}>
            🕐 {fmtDate(firstPosted.postedAt)}
            {firstPosted.postedBy && <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · {firstPosted.postedBy}</span>}
          </div>
        )}

        {/* Platform badges */}
        <div style={{ marginTop: 4 }}>
          {loading ? (
            <span style={{ fontSize: '0.65rem', color: 'var(--text3)' }}>⏳ Loading…</span>
          ) : postedPlatforms.length > 0 ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {postedPlatforms.map(pl => (
                <PlatBadge key={pl.id} pl={pl} liveUrl={pl.liveUrl} proofUrl={pl.proofUrl} />
              ))}
              <span style={{ fontSize: '0.62rem', color: 'var(--text3)', marginLeft: 2 }}>
                {postedPlatforms.length} platform{postedPlatforms.length > 1 ? 's' : ''}
              </span>
            </div>
          ) : (
            <span style={{ fontSize: '0.62rem', color: 'var(--text3)', fontStyle: 'italic' }}>
              No platforms posted yet
            </span>
          )}
        </div>

        {/* Output formats */}
        {(wo.output_formats || []).length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
            {(wo.output_formats || []).map((f, i) => (
              <span key={i} style={{
                fontSize: '0.58rem', padding: '1px 6px',
                background: 'rgba(108,88,240,0.08)',
                border: '1px solid rgba(108,88,240,0.2)',
                color: '#6c58f0', borderRadius: 20, fontWeight: 600,
              }}>{typeof f === 'object' ? f.name : f}</span>
            ))}
          </div>
        )}
      </div>

      {lightbox && (
        <ImageLightbox
          src={creative?.image_data}
          name={creative?.image_name}
          onClose={() => setLightbox(false)}
        />
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PublishedContent() {
  const [wos,      setWos]      = useState([]);
  const [clients,  setClients]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [yearF,     setYearF]     = useState('');
  const [monthsF,   setMonthsF]   = useState(new Set());
  const [showMonthDd, setShowMonthDd] = useState(false);
  const [clientF,   setClientF]   = useState('');
  const [platF,     setPlatF]     = useState('');
  const [woFilter,  setWoFilter]  = useState('');
  const [view,      setView]      = useState('grid'); // 'grid' | 'list'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { posted_only: 1 };
      if (clientF) params.client_id = clientF;
      const [woRes, clRes] = await Promise.all([
        api.get('/scheduling', { params }),
        api.get('/clients'),
      ]);
      const data = woRes.data.data || woRes.data || [];
      const posted = (Array.isArray(data) ? data : []).filter(wo => {
        const sps = wo.scheduling_platforms || [];
        return sps.some(sp => sp.state === 'posted');
      });
      setWos(posted);
      setClients(clRes.data.data || clRes.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [clientF]);

  useEffect(() => { load(); }, [load]);

  const allPlatforms = [...new Set(wos.flatMap(wo => wo.platforms || []))].sort();

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1].map(String);
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const toggleMonth = m => setMonthsF(prev => { const s = new Set(prev); s.has(m) ? s.delete(m) : s.add(m); return s; });

  useEffect(() => {
    if (!showMonthDd) return;
    const h = e => { if (!e.target.closest('[data-month-dd]')) setShowMonthDd(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showMonthDd]);

  const filtered = wos.filter(wo => {
    if (woFilter && !(wo.wo_number || '').toLowerCase().includes(woFilter.toLowerCase()) &&
        !(wo.title || '').toLowerCase().includes(woFilter.toLowerCase())) return false;
    if (platF && !(wo.platforms || []).some(p => (p||'').toLowerCase() === platF.toLowerCase())) return false;
    if (wo.event_date) {
      if (yearF && wo.event_date.slice(0, 4) !== yearF) return false;
      if (monthsF.size > 0) { const woMonth = parseInt(wo.event_date.split('-')[1], 10); if (!monthsF.has(woMonth)) return false; }
    }
    return true;
  });

  return (
    <div>
      {/* ── Sticky header ── */}
      <div style={{
        position: 'sticky', top: '-24px', zIndex: 30,
        background: 'var(--bg)', margin: '-24px -24px 20px',
        padding: '16px 24px 16px',
        borderBottom: '1px solid var(--border)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text2)' }}>
            {loading ? '⏳ Loading…' : `${filtered.length} work order${filtered.length !== 1 ? 's' : ''}`}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {/* View toggle */}
            <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              {[['grid','⊞ Grid'],['list','☰ List']].map(([v, label]) => (
                <button key={v} onClick={() => setView(v)}
                  style={{ padding: '4px 12px', fontSize: '0.72rem', fontWeight: 700,
                    border: 'none', cursor: 'pointer',
                    background: view === v ? '#6c58f0' : 'var(--bg2)',
                    color: view === v ? '#fff' : 'var(--text2)' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filters row */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={woFilter} onChange={e => setWoFilter(e.target.value)}
            placeholder="🔍 WO number or title…"
            style={{ padding: '5px 10px', fontSize: '0.78rem', borderRadius: 6,
              border: '1px solid var(--border)', background: 'var(--bg2)',
              color: 'var(--text)', width: 180 }} />

          <select value={clientF} onChange={e => setClientF(e.target.value)}
            style={{ padding: '5px 10px', fontSize: '0.78rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', cursor: 'pointer', width: 'auto' }}>
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <select value={yearF} onChange={e => setYearF(e.target.value)}
            style={{ padding: '5px 10px', fontSize: '0.78rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', cursor: 'pointer', width: 'auto' }}>
            <option value="">All Years</option>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          {/* Multi-month dropdown */}
          <div data-month-dd style={{ position: 'relative' }}>
            <button onClick={() => setShowMonthDd(v => !v)}
              style={{ padding: '5px 10px', fontSize: '0.78rem', borderRadius: 6, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
                background: monthsF.size > 0 ? '#6c58f022' : 'var(--bg2)',
                border: `1px solid ${monthsF.size > 0 ? '#6c58f0' : 'var(--border)'}`,
                color: monthsF.size > 0 ? '#6c58f0' : 'var(--text)' }}>
              📅 {monthsF.size === 0 ? 'All Months' : [...monthsF].sort((a,b)=>a-b).map(m=>MONTH_NAMES[m-1]).join(', ')}
              <span style={{ fontSize: '0.6rem', marginLeft: 2 }}>▾</span>
            </button>
            {showMonthDd && (
              <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 200,
                background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: 10, minWidth: 200 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
                  {MONTH_NAMES.map((name, i) => {
                    const m = i + 1;
                    const on = monthsF.has(m);
                    return (
                      <button key={m} onClick={() => toggleMonth(m)}
                        style={{ padding: '5px 8px', fontSize: '0.73rem', fontWeight: on ? 700 : 500,
                          borderRadius: 6, border: `1px solid ${on ? '#6c58f0' : 'var(--border)'}`,
                          background: on ? '#6c58f0' : 'var(--bg2)',
                          color: on ? '#fff' : 'var(--text2)', cursor: 'pointer' }}>
                        {name}
                      </button>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, gap: 6 }}>
                  <button onClick={() => setMonthsF(new Set())}
                    style={{ flex: 1, padding: '4px', fontSize: '0.68rem', borderRadius: 6, cursor: 'pointer',
                      border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text3)' }}>
                    Clear
                  </button>
                  <button onClick={() => setShowMonthDd(false)}
                    style={{ flex: 1, padding: '4px', fontSize: '0.68rem', fontWeight: 700, borderRadius: 6,
                      cursor: 'pointer', border: 'none', background: '#6c58f0', color: '#fff' }}>
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>

          <select value={platF} onChange={e => setPlatF(e.target.value)}
            style={{ padding: '5px 10px', fontSize: '0.78rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', cursor: 'pointer', width: 'auto' }}>
            <option value="">All Platforms</option>
            {allPlatforms.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--text3)' }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>⏳</div>
          <div style={{ fontWeight: 600 }}>Loading published content…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--text3)' }}>
          <div style={{ fontSize: '3rem', marginBottom: 12, opacity: 0.4 }}>📭</div>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text2)', marginBottom: 4 }}>
            No published content found
          </div>
          <div style={{ fontSize: '0.78rem' }}>
            {yearF || monthsF.size > 0
              ? `Nothing posted for the selected period.`
              : 'No WOs have been marked Posted yet.'}
          </div>
        </div>
      ) : view === 'grid' ? (
        /* ── Grid view ── */
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 20,
          alignItems: 'start',
        }}>
          {filtered.map(wo => <PublishedCard key={wo.id} wo={wo} />)}
        </div>
      ) : (
        /* ── List view ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(wo => <PublishedListRow key={wo.id} wo={wo} />)}
        </div>
      )}
    </div>
  );
}

// ── List row variant ──────────────────────────────────────────────────────────
function PublishedListRow({ wo }) {
  const [platRecs, setPlatRecs] = useState({});
  const [loading,  setLoading]  = useState(true);
  const [lightbox, setLightbox] = useState(false);

  const creative = wo.creative?.status === 'Approved' ? wo.creative : null;
  const [ey, em, ed] = (wo.event_date || '').split('-');

  useEffect(() => {
    api.get(`/scheduling/${wo.id}`)
      .then(r => {
        const map = {};
        (r.data.platforms || []).forEach(p => {
          const pl = findPlatformConfig(p.platform);
          const key = pl ? pl.id : p.platform;
          map[key] = { ...p, state: p.state === 'pending' ? undefined : p.state };
        });
        setPlatRecs(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [wo.id]);

  const postedPlatforms = SCH_PLATFORMS.filter(pl => {
    const ps = platRecs[pl.id] || platRecs[pl.name];
    return ps?.state === 'posted';
  }).map(pl => {
    const ps = platRecs[pl.id] || platRecs[pl.name] || {};
    return { ...pl, liveUrl: ps.live_url || ps.url || '', proofUrl: ps.proof_url || ps.proofUrl || '',
      postedAt: ps.posted_at || ps.postedAt || '', postedBy: ps.posted_by || ps.postedBy || '' };
  });

  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10,
      display: 'flex', gap: 12, alignItems: 'flex-start', padding: 12,
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    }}>
      {/* Thumbnail */}
      <div onClick={() => creative?.image_data && setLightbox(true)}
        style={{ width: 72, height: 72, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
          border: '2px solid rgba(29,184,122,0.3)', cursor: creative?.image_data ? 'zoom-in' : 'default',
          background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {creative?.image_data
          ? <img src={creative.image_data} alt="creative" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ fontSize: '1.4rem', opacity: 0.25 }}>🖼️</span>}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={{ fontFamily: 'Space Mono,monospace', fontSize: '0.68rem', fontWeight: 800,
            color: 'var(--accent)', background: 'rgba(59,126,240,0.08)',
            padding: '1px 7px', borderRadius: 5, border: '1px solid rgba(59,126,240,0.2)' }}>
            {wo.wo_number}
          </span>
          {wo.client?.name && (
            <span style={{ padding: '1px 8px', background: 'var(--bg3)', border: '1px solid var(--border2)',
              borderRadius: 20, fontSize: '0.65rem', fontWeight: 700, color: 'var(--text2)' }}>
              {wo.client.name}
            </span>
          )}
          <span style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text1)' }}>{wo.title}</span>
          {wo.event_date && (
            <span style={{ fontSize: '0.65rem', color: 'var(--text3)', fontFamily: 'Space Mono,monospace', marginLeft: 'auto' }}>
              📅 {ed}-{em}-{ey}
            </span>
          )}
        </div>

        {/* Platforms */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {loading
            ? <span style={{ fontSize: '0.65rem', color: 'var(--text3)' }}>⏳</span>
            : postedPlatforms.map(pl => (
                <div key={pl.id} style={{ display: 'flex', alignItems: 'center', gap: 4,
                  background: 'var(--bg3)', border: `1px solid ${pl.color}33`,
                  borderRadius: 6, padding: '3px 8px' }}>
                  <span style={{ fontSize: '0.65rem', fontWeight: 700, color: pl.color }}>{pl.name}</span>
                  {pl.liveUrl && (
                    <a href={pl.liveUrl} target="_blank" rel="noreferrer"
                      style={{ fontSize: '0.62rem', color: '#1db87a', fontWeight: 700, textDecoration: 'none' }}>
                      ↗ Live
                    </a>
                  )}
                  {!pl.liveUrl && pl.proofUrl && (
                    <a href={pl.proofUrl} target="_blank" rel="noreferrer"
                      style={{ fontSize: '0.62rem', color: 'var(--accent)', fontWeight: 700, textDecoration: 'none' }}>
                      🔗 Proof
                    </a>
                  )}
                  {pl.postedAt && (
                    <span style={{ fontSize: '0.58rem', color: 'var(--text3)' }}>· {fmtDate(pl.postedAt)}</span>
                  )}
                </div>
              ))
          }
        </div>
      </div>

      {lightbox && (
        <ImageLightbox src={creative?.image_data} name={creative?.image_name} onClose={() => setLightbox(false)} />
      )}
    </div>
  );
}